'use client';

import * as React from 'react';
import { format } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useVehicleClaimStats } from '@/hooks/useVehicleClaimStats';
import type {
  ClaimDailyStatusCount,
  ClaimStatusCount,
  VehicleClaimListFilters,
} from '@/types/vehicle-claim';

export interface ClaimsDashboardChartProps {
  filters?: VehicleClaimListFilters;
}

export interface ChartRow {
  date: string;
  [status_code: string]: string | number;
}

// Same tenant-configurable named colors as ClaimsStatusTiles/ClaimStatusManager,
// mapped to solid hex values since recharts' <Bar fill> can't take a Tailwind class.
const BAR_COLORS: Record<string, string> = {
  blue: '#3b82f6',
  yellow: '#eab308',
  green: '#22c55e',
  emerald: '#10b981',
  red: '#ef4444',
  orange: '#f97316',
  gray: '#6b7280',
};

function barColor(color: string | null | undefined): string {
  return (color && BAR_COLORS[color]) || BAR_COLORS.gray;
}

/**
 * Distinct status_codes present in `by_day`, ordered to match `by_status`
 * (falls back to first-seen order for any code `by_status` doesn't know about)
 * so bar stacking order stays stable across date-filter changes.
 */
export function resolveStatusOrder(
  byDay: ClaimDailyStatusCount[],
  byStatus: ClaimStatusCount[],
): string[] {
  const remaining = new Set(byDay.map((entry) => entry.status_code));
  const order: string[] = [];

  for (const status of byStatus) {
    if (remaining.has(status.status_code)) {
      order.push(status.status_code);
      remaining.delete(status.status_code);
    }
  }
  for (const entry of byDay) {
    if (remaining.has(entry.status_code)) {
      order.push(entry.status_code);
      remaining.delete(entry.status_code);
    }
  }
  return order;
}

/** Pivots the flat `{date, status_code, count}` rows into one row per date, keyed by status_code, for recharts' stacked BarChart. */
export function buildChartRows(
  byDay: ClaimDailyStatusCount[],
  statusOrder: string[],
): ChartRow[] {
  const byDate = new Map<string, ChartRow>();

  for (const entry of byDay) {
    let row = byDate.get(entry.date);
    if (!row) {
      row = { date: entry.date };
      for (const code of statusOrder) row[code] = 0;
      byDate.set(entry.date, row);
    }
    row[entry.status_code] = (row[entry.status_code] as number) + entry.count;
  }

  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function formatTick(value: string): string {
  try {
    return format(new Date(value), 'MMM dd');
  } catch {
    return value;
  }
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  let formattedDate = label;
  try {
    formattedDate = format(new Date(label), 'MMM dd, yyyy');
  } catch {
    // Use the raw label if date parsing fails.
  }

  return (
    <div className="rounded-lg border border-border-primary bg-background-primary p-3 shadow-lg">
      <p className="text-sm font-medium text-text-secondary">{formattedDate}</p>
      <ul className="mt-1 space-y-1">
        {payload.map((entry: any) => (
          <li key={entry.dataKey} className="flex items-center gap-2 text-sm">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
              aria-hidden="true"
            />
            <span className="text-text-primary">{entry.name}</span>
            <span className="font-semibold text-text-primary">{entry.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChartCardShell({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Claims by Day</CardTitle>
        <CardDescription>Daily claim volume, broken down by status</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-56" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[300px] w-full md:h-[400px]" aria-label="Loading claims chart" />
      </CardContent>
    </Card>
  );
}

/** Stacked bar chart of daily claim volume by status, driven entirely by `useVehicleClaimStats` (`by_day` for bars, `by_status` for labels/colors) — no hardcoded status list. */
export function ClaimsDashboardChart({ filters }: ClaimsDashboardChartProps) {
  const { data, isLoading, error } = useVehicleClaimStats(filters);

  if (isLoading) {
    return <ChartSkeleton />;
  }

  if (error) {
    return (
      <ChartCardShell>
        <div className="flex h-[300px] items-center justify-center md:h-[400px]">
          <p className="text-sm font-medium text-error-500">Failed to load claims chart.</p>
        </div>
      </ChartCardShell>
    );
  }

  const byDay = data?.by_day ?? [];
  const byStatus = data?.by_status ?? [];

  if (byDay.length === 0) {
    return (
      <ChartCardShell>
        <div className="flex h-[300px] items-center justify-center text-sm text-text-secondary md:h-[400px]">
          No claims in this date range.
        </div>
      </ChartCardShell>
    );
  }

  const statusMeta = new Map(byStatus.map((status) => [status.status_code, status]));
  const statusOrder = resolveStatusOrder(byDay, byStatus);
  const rows = buildChartRows(byDay, statusOrder);

  return (
    <ChartCardShell>
      <ResponsiveContainer width="100%" height={300} className="md:h-[400px]">
        <BarChart data={rows} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatTick}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickLine={{ stroke: '#d1d5db' }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickLine={{ stroke: '#d1d5db' }}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <Tooltip content={<ChartTooltip />} />
          <Legend
            iconType="circle"
            formatter={(value: string) => <span className="text-sm text-text-primary">{value}</span>}
          />
          {statusOrder.map((code) => (
            <Bar
              key={code}
              dataKey={code}
              name={statusMeta.get(code)?.label ?? code}
              stackId="claims"
              fill={barColor(statusMeta.get(code)?.color)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCardShell>
  );
}
